import React from "react";

import { AlertTriangle } from "lucide-react";
import type { FallbackProps } from "react-error-boundary";

import Button from "../Button";

import "./style.scss";

/**
 * Top-level fallback shown when a render error escapes to the root ErrorBoundary.
 * Rendered outside the Router, so navigation uses window.location rather than
 * router hooks. No stack traces are surfaced to end users.
 */
const AppErrorFallback: React.FC<FallbackProps> = ({ resetErrorBoundary }) => {
  const handleReload = () => {
    resetErrorBoundary();
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.assign("/");
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-100 px-4">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 rounded-full bg-indigo-200/35 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-64 w-64 rounded-full bg-cyan-200/30 blur-3xl" />
      </div>

      <div className="relative mx-auto flex min-h-[calc(100vh-64px)] w-full max-w-4xl items-center justify-center">
        <div className="app-error-card relative w-full max-w-2xl rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-[0_18px_45px_rgba(15,23,42,0.10)] sm:p-12">
          <div className="relative mx-auto mb-6 inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-4 py-1.5 text-sm font-semibold text-amber-700">
            <AlertTriangle size={16} />
            Something went wrong
          </div>

          <div className="relative mb-3 text-3xl font-extrabold leading-tight tracking-tight text-slate-900 sm:text-4xl">
            This page ran into a problem
          </div>
          <p className="relative mx-auto mb-8 max-w-xl text-base leading-8 text-slate-600 sm:text-[17px]">
            An unexpected error occurred while rendering this page. You can try
            reloading, or head back to the home page.
          </p>

          <div className="mx-auto mb-9 h-px w-full max-w-md bg-slate-200" />

          <div className="button-wrapper">
            <Button
              type="button"
              onClick={handleReload}
              className="reload-button"
            >
              Reload
            </Button>
            <Button
              type="button"
              onClick={handleGoHome}
              className="home-button"
              variant="white"
            >
              Go home
            </Button>
          </div>

          <div className="mt-6 text-sm leading-6 text-slate-500">
            If this issue continues, please contact your administrator.
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppErrorFallback;
